/**
 * Command palette — the Ito Research command menu.
 *
 * Triggered by `Mod+K` / backtick (see `lib/hotkeys`). Features:
 *
 * - Multi-level menus: a node with children opens a submenu; `←` goes back.
 * - Fuzzy search across every command via `commandFilter` (see `lib/fuzzy`),
 *   with the breadcrumb path shown under each hit.
 * - Live securities search against the instruments-master index while typing.
 * - Live workspace (canvas + page) search; selecting one opens it as a tab.
 * - `↵` runs the selected command; `Esc` closes.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  formatForDisplay,
  useHotkeyRegistrations,
} from "@tanstack/react-hotkeys";
import {
  Badge,
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@itoresearch/design-system";
import { ArrowLeft, ChevronRight, CornerDownLeft } from "lucide-react";

import { commandFilter } from "../../lib/fuzzy";
import { useDebouncedValue } from "../../hooks/use-debounced-value";
import { useCommands, type CommandNode } from "./commands";
import { useSecuritiesSearch } from "./securities-search";
import { useWorkspacesSearch } from "./workspaces-search";
import { openWorkspaceTab } from "../terminal/tabs-store";
import { WorkspacePicker } from "../terminal/workspace-picker";
import type { SearchHit, WorkspaceHit } from "../../lib/search";

interface CommandMenuContextValue {
  /** Open the palette (also opens a specific submenu when `pageId` is given). */
  open: () => void;
  /** Open the workspace picker dialog (search/select/create a tab). */
  openWorkspacePicker: () => void;
}

/** The keyboard-shortcuts node id (see `useCommands`). */
const SHORTCUTS_NODE_ID = "shortcuts";

const CommandMenuContext = createContext<CommandMenuContextValue | null>(null);

export function useCommandMenu(): CommandMenuContextValue {
  const ctx = useContext(CommandMenuContext);
  if (!ctx)
    throw new Error("useCommandMenu must be used within a CommandMenuProvider");
  return ctx;
}

/** Owns the open state and mounts the palette. Place near the app root. */
export function CommandMenuProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const openMenu = useCallback(() => setOpen(true), []);
  const openWorkspacePicker = useCallback(() => setPickerOpen(true), []);

  return (
    <CommandMenuContext.Provider value={{ open: openMenu, openWorkspacePicker }}>
      {children}
      <CommandMenu open={open} onOpenChange={setOpen} />
      <WorkspacePicker open={pickerOpen} onOpenChange={setPickerOpen} />
    </CommandMenuContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/** A command together with its breadcrumb path (root → node titles). */
interface FlatEntry {
  node: CommandNode;
  path: string[];
}

function flattenCommands(
  nodes: CommandNode[],
  path: string[] = [],
): FlatEntry[] {
  const out: FlatEntry[] = [];
  for (const node of nodes) {
    const next = [...path, node.title];
    out.push({ node, path: next });
    if (node.children?.length) {
      out.push(...flattenCommands(node.children, next));
    }
  }
  return out;
}

function findNode(nodes: CommandNode[], id: string): CommandNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children?.length) {
      const found = findNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

/** Group a level's nodes by their `group` label, preserving order. */
function groupNodes(
  nodes: CommandNode[],
): Array<{ label: string; nodes: CommandNode[] }> {
  const groups: Array<{ label: string; nodes: CommandNode[] }> = [];
  const byLabel = new Map<string, CommandNode[]>();
  for (const node of nodes) {
    const label = node.group ?? "Commands";
    const bucket = byLabel.get(label) ?? [];
    bucket.push(node);
    byLabel.set(label, bucket);
    if (groups.every((g) => g.label !== label))
      groups.push({ label, nodes: bucket });
  }
  return groups;
}

interface CommandMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CommandMenu({ open, onOpenChange }: CommandMenuProps) {
  const navigate = useNavigate();
  const commands = useCommands();

  const [search, setSearch] = useState("");
  const [pageId, setPageId] = useState<string | null>(null);
  // Debounce the securities/workspace queries so keystrokes don't hammer the
  // search index.
  const debouncedSearch = useDebouncedValue(search, 150);

  const page = useMemo(
    () => (pageId ? findNode(commands, pageId) : null),
    [commands, pageId],
  );
  const flat = useMemo(() => flattenCommands(commands), [commands]);
  // When searching, only actionable leaves are shown. Parent nodes ("Go to…",
  // "Theme…", …) exist only to open a submenu, so they'd just shadow the very
  // commands the user is looking for; the breadcrumb path keeps context.
  const searchResults = useMemo(
    () => flat.filter((entry) => !entry.node.children?.length),
    [flat],
  );
  const isSearching = search.trim().length > 0;

  // Reset navigation + search each time the palette opens.
  useEffect(() => {
    if (open) {
      setSearch("");
      setPageId(null);
    }
  }, [open]);

  const securities = useSecuritiesSearch(debouncedSearch);
  const workspaces = useWorkspacesSearch(debouncedSearch);
  const liveSearchActive = isSearching && debouncedSearch.trim().length >= 2;
  // The live search sections only render rows once they actually have hits
  // (see `WorkspacesGroup`/`SecuritiesGroup`); the separator must follow suit
  // so a fruitless query doesn't leave a dangling rule after the commands.
  const hasLiveResults =
    liveSearchActive &&
    ((workspaces.data?.hits.length ?? 0) > 0 ||
      (securities.data?.hits.length ?? 0) > 0);
  // cmdk's `CommandEmpty` only counts registered (non-forceMounted) items —
  // the live search groups are forceMounted, so cmdk would render "No results"
  // even while they have hits. Suppress it exactly when a live section has
  // rendered rows; otherwise it is the single empty state (there is no
  // separate per-section "no matches" row anymore).
  const showEmpty = pageId !== SHORTCUTS_NODE_ID && !hasLiveResults;

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const goBack = useCallback(() => {
    setPageId(null);
    setSearch("");
  }, []);

  const selectCommand = useCallback(
    (node: CommandNode) => {
      if (node.children?.length) {
        setPageId(node.id);
        setSearch("");
        return;
      }
      // The shortcuts node renders a live registry instead of running an action.
      if (node.id === SHORTCUTS_NODE_ID) {
        setPageId(SHORTCUTS_NODE_ID);
        setSearch("");
        return;
      }
      node.onSelect?.();
      close();
    },
    [close],
  );

  const selectSecurity = useCallback(
    (hit: SearchHit) => {
      // Only ticker-bearing hits map to a resolvable /security/<symbol> route;
      // a free-text name (FX pair, bond, …) would not resolve server-side.
      if (hit.ticker) navigate({ to: "/", search: { symbol: hit.ticker } });
      close();
    },
    [navigate, close],
  );

  const selectWorkspace = useCallback(
    (hit: WorkspaceHit) => {
      // One workspace == one terminal tab; the store owns localStorage + focus.
      openWorkspaceTab(hit.workspaceId, { title: hit.title, kind: hit.kind });
      close();
    },
    [close],
  );

  const pageChildren: CommandNode[] = page?.children ?? commands;
  const groups = isSearching ? [] : groupNodes(pageChildren);

  return (
    <CommandDialog open={open} onOpenChange={close}>
      <Command
        filter={commandFilter}
        loop
        onKeyDown={(event) => {
          // Left arrow in a submenu goes back to the root (cmdk runs our
          // handler first and leaves unhandled keys alone).
          if (!isSearching && page && event.key === "ArrowLeft") {
            event.preventDefault();
            goBack();
          }
        }}
      >
        {!isSearching && page && (
          <div className="flex items-center gap-1 border-b px-3 py-2 text-xs text-muted-foreground">
            <button
              type="button"
              onClick={goBack}
              className="inline-flex items-center gap-1 rounded-sm px-1 py-0.5 hover:bg-muted hover:text-foreground"
            >
              <ArrowLeft className="size-3.5" />
              All commands
            </button>
            <span className="opacity-50">/</span>
            <span className="font-medium text-foreground">{page.title}</span>
          </div>
        )}

        <CommandInput
          value={search}
          onValueChange={setSearch}
          autoFocus
          placeholder={
            page && !isSearching
              ? `Search in ${page.title}…`
              : "Type a command or search securities and workspaces…"
          }
        />

        <CommandList>
          {isSearching ? (
            <>
              {searchResults.map(({ node, path }) => (
                <CommandItem
                  key={node.id}
                  value={node.title}
                  keywords={[
                    ...(node.keywords ?? []),
                    ...path.slice(0, -1),
                    node.description ?? "",
                  ]}
                  onSelect={() => selectCommand(node)}
                >
                  {node.icon && <node.icon />}
                  <span className="flex-1 truncate">
                    {node.title}
                    {node.children?.length ? "…" : ""}
                  </span>
                  {node.children?.length ? (
                    <ChevronRight className="size-3.5 text-muted-foreground" />
                  ) : (
                    <span className="truncate text-xs text-muted-foreground">
                      {path.length > 1
                        ? path.slice(0, -1).join(" › ")
                        : node.description}
                    </span>
                  )}
                  {node.shortcut && (
                    <CommandShortcut>{node.shortcut}</CommandShortcut>
                  )}
                </CommandItem>
              ))}
              {hasLiveResults && <CommandSeparator alwaysRender />}
              <WorkspacesGroup
                query={debouncedSearch}
                isSearching={isSearching}
                onSelect={selectWorkspace}
                workspaces={workspaces}
              />
              <SecuritiesGroup
                query={debouncedSearch}
                isSearching={isSearching}
                onSelect={selectSecurity}
                securities={securities}
              />
            </>
          ) : pageId === SHORTCUTS_NODE_ID ? (
            <ShortcutsPage />
          ) : (
            groups.map(({ label, nodes }) => (
              <CommandGroup key={label} heading={label}>
                {nodes.map((node) => (
                  <CommandItem
                    key={node.id}
                    value={node.title}
                    keywords={[
                      ...(node.keywords ?? []),
                      node.description ?? "",
                    ]}
                    onSelect={() => selectCommand(node)}
                  >
                    {node.icon && <node.icon />}
                    <span className="flex-1 truncate">{node.title}</span>
                    {node.description && (
                      <span className="truncate text-xs text-muted-foreground">
                        {node.description}
                      </span>
                    )}
                    {node.children?.length && (
                      <ChevronRight className="size-3.5 text-muted-foreground" />
                    )}
                    {node.shortcut && (
                      <CommandShortcut>{node.shortcut}</CommandShortcut>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))
          )}
          {showEmpty && <CommandEmpty>No results for “{search}”.</CommandEmpty>}
        </CommandList>

        <div className="flex items-center gap-3 border-t px-3 py-1.5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <kbd className="rounded-sm border bg-muted px-1 font-mono">↑↓</kbd>{" "}
            navigate
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="rounded-sm border bg-muted px-1 font-mono">
              <CornerDownLeft className="size-3" />
            </kbd>{" "}
            select
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="rounded-sm border bg-muted px-1 font-mono">esc</kbd>{" "}
            close
          </span>
        </div>
      </Command>
    </CommandDialog>
  );
}

// ---------------------------------------------------------------------------
// Workspace search section
// ---------------------------------------------------------------------------

function WorkspacesGroup({
  query,
  isSearching,
  onSelect,
  workspaces,
}: {
  query: string;
  isSearching: boolean;
  onSelect: (hit: WorkspaceHit) => void;
  workspaces: ReturnType<typeof useWorkspacesSearch>;
}) {
  const { data, isPending, isError } = workspaces;
  const active = isSearching && query.trim().length >= 2;

  if (!active) return null;

  const hits = data?.hits ?? [];
  // The palette only shows something once there are actual hits. While the
  // query is in flight (or has failed — e.g. an unready index) there is
  // nothing actionable to render, so the section stays hidden instead of
  // flashing a "Searching…" / "unavailable" row under every search. Command
  // results may still be present, so no empty state is shown either.
  if (isPending || isError || hits.length === 0) return null;

  return (
    // `forceMount` keeps the group in the accessibility tree even though its
    // rows are matched by the custom filter, mirroring `SecuritiesGroup`.
    <CommandGroup heading="Workspaces" forceMount>
      {hits.map((hit) => (
        <CommandItem
          key={hit.workspaceId}
          forceMount
          // cmdk keys items by `value` — a duplicate value would highlight
          // every colliding row at once, so make each hit unique.
          value={`${hit.title} ${hit.slug ?? ""} ${hit.workspaceId}`}
          keywords={[hit.kind, hit.slug ?? "", hit.description ?? ""]}
          onSelect={() => onSelect(hit)}
        >
          <Badge variant="outline" className="font-mono">
            {hit.kind}
          </Badge>
          <span className="flex-1 truncate">{hit.title}</span>
          {hit.description && (
            <span className="truncate text-xs text-muted-foreground">
              {hit.description}
            </span>
          )}
          {hit.slug && (
            <span className="truncate text-xs text-muted-foreground">
              /{hit.slug}
            </span>
          )}
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

// ---------------------------------------------------------------------------
// Securities search section
// ---------------------------------------------------------------------------

function SecuritiesGroup({
  query,
  isSearching,
  onSelect,
  securities,
}: {
  query: string;
  isSearching: boolean;
  onSelect: (hit: SearchHit) => void;
  securities: ReturnType<typeof useSecuritiesSearch>;
}) {
  const { data, isPending, isError } = securities;
  const active = isSearching && query.trim().length >= 2;

  if (!active) return null;

  const hits = data?.hits ?? [];
  // Only render the section when there are actual hits — see `WorkspacesGroup`.
  if (isPending || isError || hits.length === 0) return null;

  return (
    // `forceMount` keeps the group in the accessibility tree even though its
    // rows are matched by the custom filter.
    <CommandGroup heading="Securities" forceMount>
      {hits.map((hit) => (
        <CommandItem
          key={hit.objectId}
          forceMount
          // cmdk keys items by `value` — a duplicate value would highlight
          // every colliding row at once, so make each hit unique.
          value={`${hit.name} ${hit.ticker ?? ""} ${hit.objectId}`}
          keywords={[
            hit.ticker ?? "",
            ...(hit.identifiers ?? []),
            hit.instrumentType ?? "",
            ...(hit.venues ?? []),
          ]}
          onSelect={() => onSelect(hit)}
        >
          <Badge variant="outline" className="font-mono">
            {hit.ticker ?? hit.kind}
          </Badge>
          <span className="flex-1 truncate">{hit.name}</span>
          <span className="truncate text-xs text-muted-foreground">
            {hit.instrumentType ?? hit.kind}
          </span>
          {hit.currency && (
            <span className="text-xs text-muted-foreground">
              {hit.currency}
            </span>
          )}
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

// ---------------------------------------------------------------------------
// Keyboard shortcuts page
// ---------------------------------------------------------------------------

/** Live view of every registered hotkey + sequence (see `lib/hotkeys`). */
function ShortcutsPage() {
  const { hotkeys, sequences } = useHotkeyRegistrations();

  const entries = [
    ...hotkeys.map((reg) => ({
      id: reg.id,
      name: reg.options.meta?.name ?? String(reg.hotkey),
      description: reg.options.meta?.description,
      keys: formatForDisplay(reg.hotkey),
    })),
    ...sequences.map((reg) => ({
      id: reg.id,
      name: reg.options.meta?.name ?? reg.sequence.join(" "),
      description: reg.options.meta?.description,
      keys: reg.sequence.map((s) => formatForDisplay(s)).join("  "),
    })),
  ];

  if (entries.length === 0) {
    return (
      <CommandGroup heading="Keyboard shortcuts">
        <CommandItem forceMount disabled>
          No shortcuts registered yet.
        </CommandItem>
      </CommandGroup>
    );
  }

  return (
    <CommandGroup heading="Keyboard shortcuts">
      {entries.map((entry) => (
        <CommandItem
          key={entry.id}
          forceMount
          // Hotkeys and sequences can share a name ("Go to Box" via Alt+2 and
          // g b); cmdk keys items by value, so make it unique.
          value={`${entry.name} ${entry.keys}`}
          keywords={[entry.name, entry.description ?? ""]}
          onSelect={() => undefined}
        >
          <span className="flex-1 truncate">{entry.name}</span>
          {entry.description && (
            <span className="truncate text-xs text-muted-foreground">
              {entry.description}
            </span>
          )}
          <CommandShortcut>{entry.keys}</CommandShortcut>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}
